/**
 * Initialize Sticky player
 *
 * @param {*} players player list in page = { playerList, playerConfigList }
 */
import { VIDEO_PLAYER_OBSERVER_OPTIONS, VIDEO_PLAYER_OBSERVER_OPTIONS_MOBILE } from '../video-player/constants';
import { isMobile, isWidthDeviceMobileTablet } from '../../utils/device-utils';
import { isLandscape } from '../../utils/mediaquery-utils';
import { COMMON_STATE_KEYS } from '../../global-store/subjects/common/initial-state';
import { getCommonStorePartial } from '../../global-store/subjects/common';
import { heroDisclaimer } from '../hero-disclaimer/hero-disclaimer';

import {
  createPlaceholderDiv,
  disableStickyElement,
  disableStickyElementAttributes,
  enableStickyElement,
  enableStickyElementAttributes,
  getMobileTreshold,
  getVideoPlayer,
  isLMPArticle,
} from './helpers';
import { initVideoPlayerListeners } from './video-player-listeners';
import { stickyHeroPlayerState } from './state';
import { $globalNav, $mediaWrapper, $player, $playerContainer, $sectionDividerSpecial } from './constants';

export const stickyHeroPlayerDisclaimer = heroDisclaimer({
  elementToAppendTo: isMobile() ? $mediaWrapper : $player,
});

export const init = (players) => {
  // listener to avoid sticky player on mobile landscape
  const heroPlayerIstance = getVideoPlayer(players);
  const handleDeviceOrientation = () => {
    window.addEventListener('resize', () => {
      if (isLandscape()) {
        disableStickyElement();
        disableStickyElementAttributes(players);
      } else if (
        !stickyHeroPlayerState.getState('hasUserRejectedSticky') &&
        !stickyHeroPlayerState.getState('shouldNotSticky') &&
        !isLandscape() &&
        stickyHeroPlayerState.getState('isTresholdPassed') &&
        !heroPlayerIstance?.getFullscreen()
      ) {
        enableStickyElement();
      }
    });
  };

  initVideoPlayerListeners(players);

  if (isWidthDeviceMobileTablet()) {
    handleDeviceOrientation();

    const playerContainerObserverMobile = new IntersectionObserver((entries) => {
      entries.forEach((elem) => {
        if (
          isLandscape() ||
          stickyHeroPlayerState.getState('hasUserRejectedSticky') ||
          stickyHeroPlayerState.getState('shouldNotSticky')
        ) {
          return;
        }
        const intersectionRatioValue = getMobileTreshold();
        if (elem.intersectionRatio < intersectionRatioValue) {
          if (!isLMPArticle) {
            createPlaceholderDiv();
          }
          enableStickyElement();
          enableStickyElementAttributes(players);
          stickyHeroPlayerState.setState('isTresholdPassed', true);
        } else if (elem.intersectionRatio >= intersectionRatioValue) {
          disableStickyElement();
          disableStickyElementAttributes(players);
          stickyHeroPlayerState.setState('isTresholdPassed', false);
        }
      });
    }, VIDEO_PLAYER_OBSERVER_OPTIONS_MOBILE);

    if (isLMPArticle) {
      playerContainerObserverMobile.observe($playerContainer);
    } else {
      playerContainerObserverMobile.observe($sectionDividerSpecial || $globalNav);
    }
  } else {
    const playerContainerObserverDesktop = new IntersectionObserver((entries) => {
      const elementTreshold = isLMPArticle ? 0.5 : 0.3;
      entries.forEach((elem) => {
        if (
          stickyHeroPlayerState.getState('hasUserRejectedSticky') ||
          stickyHeroPlayerState.getState('shouldNotSticky')
        ) {
          return;
        }
        if (elem.intersectionRatio < elementTreshold) {
          enableStickyElement();
          enableStickyElementAttributes(players);
        } else if (elem.intersectionRatio >= elementTreshold) {
          disableStickyElement();
          disableStickyElementAttributes(players);
        }
      });
    }, VIDEO_PLAYER_OBSERVER_OPTIONS);

    playerContainerObserverDesktop.observe($playerContainer);
  }

  // Toggle depending on the HP injected
  const isInjectedHPStore = getCommonStorePartial(COMMON_STATE_KEYS.IS_INJECTED_HP);
  isInjectedHPStore.subscribe((isInjectedHP) => {
    if (isInjectedHP) {
      stickyHeroPlayerState.setState('hasUserRejectedSticky', true);
      disableStickyElementAttributes(players);
      if (isWidthDeviceMobileTablet()) {
        disableStickyElement();
      } else {
        disableStickyElement();
      }
    }
  });
};
